import React, { useState } from 'react';
import { Pencil, Trash2, Plus, X } from 'lucide-react';
import { GroupTag } from '../../types/protocols';
import { useBuildingTags } from '../../hooks/useBuildingTags';

type ModalState =
    | { mode: 'closed' }
    | { mode: 'add' }
    | { mode: 'edit'; tag: GroupTag };

const BuildingGroupTagsManagementPage: React.FC = () => {
    const { tags, loading, error, setError, usageByTag, buildingsByTag, addTag, updateTag, deleteTag } = useBuildingTags();
    const [modalState, setModalState] = useState<ModalState>({ mode: 'closed' });
    const [modalLabel, setModalLabel] = useState('');
    const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
    const [usageTag, setUsageTag] = useState<GroupTag | null>(null);
    const [saving, setSaving] = useState(false);

    const openAdd = () => {
        setModalLabel('');
        setModalState({ mode: 'add' });
    };

    const openEdit = (tag: GroupTag) => {
        setModalLabel(tag.tag);
        setModalState({ mode: 'edit', tag });
    };

    const closeModal = () => {
        setModalState({ mode: 'closed' });
        setModalLabel('');
    };

    const handleSave = async () => {
        const label = modalLabel.trim();
        if (!label) return;

        setSaving(true);
        try {
            if (modalState.mode === 'add') {
                await addTag(label);
            } else if (modalState.mode === 'edit') {
                await updateTag(modalState.tag.key, label);
            }
            closeModal();
        } catch {
            // Error surfaced via hook state; keep modal open so the user can retry.
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (key: string) => {
        try {
            await deleteTag(key);
            setDeleteConfirm(null);
        } catch {
            // Error surfaced via hook state.
        }
    };

    if (loading) {
        return (
            <div className="text-center py-4">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Laddar...</span>
                </div>
            </div>
        );
    }

    return (
        <div style={{ maxWidth: 600 }}>
            <p className="text-muted mb-4">
                Hantera byggnadsgrupper som används för att gruppera och filtrera byggnader.
            </p>

            {error && (
                <div className="alert alert-danger alert-dismissible" role="alert">
                    {error}
                    <button type="button" className="btn-close" onClick={() => setError(null)} />
                </div>
            )}

            {/* Tag list */}
            <div className="list-group mb-4">
                {tags.map((tag) => {
                    const usage = usageByTag[tag.key] ?? 0;
                    return (
                        <div key={tag.key} className="list-group-item d-flex align-items-center gap-2">
                            {deleteConfirm === tag.key ? (
                                <>
                                    <span className="flex-grow-1 text-danger" style={{ fontSize: '13px' }}>
                                        {usage > 0
                                            ? `Används av ${usage} ${usage === 1 ? 'byggnad' : 'byggnader'}. Gruppen tas bort från dessa. Ta bort?`
                                            : 'Ta bort byggnadsgruppen?'}
                                    </span>
                                    <button
                                        className="btn btn-sm btn-danger"
                                        onClick={() => handleDelete(tag.key)}
                                    >
                                        Ta bort
                                    </button>
                                    <button
                                        className="btn btn-sm btn-outline-secondary"
                                        onClick={() => setDeleteConfirm(null)}
                                    >
                                        Avbryt
                                    </button>
                                </>
                            ) : (
                                <>
                                    <span className="flex-grow-1">{tag.tag}</span>
                                    {usage > 0 && (
                                        <button
                                            type="button"
                                            className="badge bg-light text-muted border-0"
                                            onClick={() => setUsageTag(tag)}
                                            title="Visa byggnader"
                                        >
                                            {usage}
                                        </button>
                                    )}
                                    <button
                                        className="btn btn-sm btn-outline-secondary"
                                        onClick={() => openEdit(tag)}
                                        title="Redigera"
                                    >
                                        <Pencil size={14} />
                                    </button>
                                    <button
                                        className="btn btn-sm btn-outline-danger"
                                        onClick={() => setDeleteConfirm(tag.key)}
                                        title="Ta bort"
                                    >
                                        <Trash2 size={14} />
                                    </button>
                                </>
                            )}
                        </div>
                    );
                })}

                {tags.length === 0 && (
                    <div className="list-group-item text-muted text-center">
                        Inga byggnadsgrupper skapade
                    </div>
                )}
            </div>

            {/* Add button */}
            <button
                className="btn btn-primary d-flex align-items-center gap-1"
                onClick={openAdd}
            >
                <Plus size={16} />
                Lägg till byggnadsgrupp
            </button>

            {/* Usage modal: buildings using this tag */}
            {usageTag && (
                <div
                    className="modal show d-block"
                    style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
                    onClick={() => setUsageTag(null)}
                >
                    <div
                        className="modal-dialog modal-dialog-centered modal-dialog-scrollable"
                        onClick={e => e.stopPropagation()}
                    >
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Byggnader i "{usageTag.tag}"</h5>
                                <button type="button" className="btn-close" onClick={() => setUsageTag(null)} />
                            </div>
                            <div className="modal-body">
                                <div className="list-group">
                                    {(buildingsByTag[usageTag.key] ?? []).map((b) => (
                                        <a
                                            key={b.key}
                                            href={`/building/${b.key}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="list-group-item list-group-item-action"
                                        >
                                            {b.name?.trim() || 'Namnlös byggnad'}
                                        </a>
                                    ))}
                                    {(buildingsByTag[usageTag.key] ?? []).length === 0 && (
                                        <div className="list-group-item text-muted text-center">
                                            Inga byggnader använder denna grupp
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setUsageTag(null)}>
                                    Stäng
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Add / Edit modal */}
            {modalState.mode !== 'closed' && (
                <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered modal-sm">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    {modalState.mode === 'add' ? 'Ny byggnadsgrupp' : 'Redigera byggnadsgrupp'}
                                </h5>
                                <button type="button" className="btn-close" onClick={closeModal} />
                            </div>
                            <form onSubmit={e => { e.preventDefault(); handleSave(); }}>
                                <div className="modal-body">
                                    <label htmlFor="groupTagLabel" className="form-label">Namn</label>
                                    <input
                                        id="groupTagLabel"
                                        type="text"
                                        className="form-control"
                                        value={modalLabel}
                                        onChange={e => setModalLabel(e.target.value)}
                                        placeholder="Namn på byggnadsgrupp"
                                        autoFocus
                                        required
                                    />
                                </div>
                                <div className="modal-footer">
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={closeModal}
                                        disabled={saving}
                                    >
                                        <X className="me-1" size={14} />
                                        Avbryt
                                    </button>
                                    <button
                                        type="submit"
                                        className="btn btn-primary"
                                        disabled={!modalLabel.trim() || saving}
                                    >
                                        {saving ? 'Sparar...' : modalState.mode === 'add' ? 'Lägg till' : 'Spara'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BuildingGroupTagsManagementPage;
